import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import useTranslation from 'next-translate/useTranslation';
import loadNamespaces from 'next-translate/loadNamespaces';
import Image from 'next/image';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import { Button, Col, Row } from '../../VuexyDesign/atoms/_index';
import { TextBold32, TextRegular18 } from '../../VuexyDesign/atoms/Text';
import { useIsLoggedIn, useRafflesByLabel } from '../../api';
import RaffleCard from '../../components/Contest/Card';
import Loader from '../../components/common/Loader';
import { useIsMobile } from '../../style/constants';
import BrowserPageTitle from '../../components/common/BrowserPageTitle';
import AccordionContent from '../../components/AmsterdamDanceEvent/AccordionContent';
import CtaBanner from '../../VuexyDesign/molecules/CtaBanner';
import { VirpPageContext } from '../_app';

const ResponsiveCard = styled(RaffleCard)`
  margin: 10px;
  && {
    width: auto;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  &:nth-child(n + 2) {
    margin-top: 30px;
  }
`;

const AdeContestPage = () => {
  const AdeLabelId = 104578;
  const { t } = useTranslation('contests');
  const isMobile = useIsMobile();
  const userLoggedIn = useIsLoggedIn();

  const videos = [
    {
      key: 1,
      src: 'https://www.youtube.com/embed/TYNWik7PF_c?si=E4EPIbZAOv2Z1syG',
    },
    {
      key: 2,
      src: 'https://www.youtube.com/embed/KH3hKH2w4x8?si=FcSCFPv6KdhhTjf1',
    },
    {
      key: 3,
      src: 'https://www.youtube.com/embed/fqoc2MI5W0s?si=waaUU4UTHPyl0jIM',
    },
  ];

  const {
    data: contest,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRafflesByLabel(AdeLabelId, {
    filter: 'all',
  });

  const sorted_contest = useMemo(
    () => orderBy(contest, ['closed_at'], ['desc']),
    [contest],
  );

  return (
    <div className="w-100 d-flex flex-column align-items-center">
      <BrowserPageTitle description={t('contests:adeMetaTitle') as string}>
        {t('contests:adeMetaDescription') as string}
      </BrowserPageTitle>
      <div className={`${isMobile ? 'w-100' : 'w-75'}`}>
        <Row className={`${isMobile ? 'px-0 mx-0' : ''}`}>
          <Col className={`${isMobile ? 'px-0' : ''}`}>
            <div
              className={`text-white px-2  d-flex align-items-start justify-content-center flex-column ${
                isMobile ? '' : 'mt-4'
              }`}
              style={{
                borderRadius: isMobile ? '0' : '20px',
                backgroundImage: 'url(/static/images/AdeContest.webp)',
                backgroundSize: 'cover',
                height: '300px',
              }}
            >
              <Image
                src="/static/images/AdeLogo.svg"
                height={60}
                width={60}
                alt="ade-contest"
              />
              <h1 className="text-white mt-1" style={{ fontSize: '3em' }}>
                {t('contests:virppXAde')}
              </h1>
              <TextRegular18>{t('contests:joinAdeDemoDrop')}</TextRegular18>
            </div>
          </Col>
        </Row>
      </div>

      <div className={`${isMobile ? 'w-100 px-1' : 'w-75'}`}>
        <Row className="my-3">
          <Col>
            <div className="d-flex flex-column mb-2">
              <TextBold32>{t('contests:demoDrops')}</TextBold32>
              <TextRegular18>{t('contests:submitToAde')}</TextRegular18>
            </div>
            <>
              <div
                className={`d-flex flex-wrap ${
                  isLoading ? 'justify-content-center' : ''
                }`}
                style={{ marginLeft: '-1rem', marginRight: '-1rem' }}
              >
                {isLoading &&
                  !(
                    Array.isArray(sorted_contest) && sorted_contest.length > 0
                  ) && (
                    <Section>
                      <Loader />
                    </Section>
                  )}
                {sorted_contest.map((raffle) => (
                  <Grid item xs={12} sm={12} md={6} lg={4} key={raffle.id}>
                    <ResponsiveCard raffle={raffle} key={raffle.id} />
                  </Grid>
                ))}
              </div>
              <div className="d-flex justify-content-center">
                {hasNextPage && (
                  <Button
                    className="mt-1"
                    outline
                    color="primary"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    <>{t('contests:loadMore') as string}</>
                  </Button>
                )}
              </div>
            </>
          </Col>
        </Row>
      </div>

      <div
        className="w-100 d-flex align-items-center flex-column"
        style={{ backgroundColor: '#F8F8F8' }}
      >
        <div className={`${isMobile ? 'w-100 px-1' : 'w-75'} my-3`}>
          <Row>
            <Col lg={12}>
              <div className="d-flex flex-column mb-2">
                <TextBold32>{t('contests:highlights')}</TextBold32>
                <TextRegular18>
                  {t('contests:reliveUnforgettableMoments')}
                </TextRegular18>
              </div>
              <div
                className="d-flex flex-wrap"
                style={{ marginLeft: '-1rem', marginRight: '-1rem' }}
              >
                {videos.map((video) => (
                  <Grid item xs={12} sm={12} md={6} lg={4} key={video.key}>
                    <div
                      style={{ padding: isMobile ? '0' : '10px' }}
                      className="py-1 px-1"
                    >
                      <Video
                        loading="lazy"
                        title="VIRPP X ADE"
                        className="rounded w-100"
                        style={{ height: isMobile ? '20rem' : '20rem' }}
                        frameBorder="0"
                        src={video.src}
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  </Grid>
                ))}
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="w-100 d-flex align-items-center flex-column">
        <div className={`${isMobile ? 'w-100 px-1' : 'w-75'} my-3`}>
          <Row>
            <Col lg={12} className="d-flex align-items-center flex-column">
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:adeDemoDrop')}
                </h2>
                <TextRegular18>
                  {t('contests:amsterdamDanceEvent')}
                </TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:theCruxOfAde')}
                </h2>
                <TextRegular18>{t('contests:notJustAProgram')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:theConfluenceOfCreativity')}
                </h2>
                <TextRegular18>{t('contests:yearAfterYear')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:embracingDiversity')}
                </h2>
                <TextRegular18>{t('contests:esteemedLabels')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:virppYourDigitalBridge')}
                </h2>
                <TextRegular18>{t('contests:theProcess')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:fromBeatsToStardom')}
                </h2>
                <TextRegular18>{t('contests:countlessStories')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:echoingBeyond')}
                </h2>
                <TextRegular18>{t('contests:rippleEffect')}</TextRegular18>
              </div>
              <div
                className={`d-flex justify-content-center ${
                  isMobile ? 'w-100' : 'w-75'
                } flex-column mt-3`}
              >
                <h2 style={{ fontSize: '2em' }} className="font-weight-bolder">
                  {t('contests:theRoadAhead')}
                </h2>
                <TextRegular18>
                  {t('contests:theAdeDemoDropInitiative')}
                </TextRegular18>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div
        className="w-100 d-flex align-items-center flex-column"
        style={{ backgroundColor: '#F8F8F8' }}
      >
        <div className={`${isMobile ? 'w-100 px-1' : 'w-75'} mb-2`}>
          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-center w-100">
                <AccordionContent />
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="mx-1 mt-3" style={{ marginBottom: '-6rem' }}>
        <CtaBanner
          title={t('home:unlockYourMusicPotential') as string}
          subtitle={t('home:joinTheCommunity') as string}
          button
          buttonColor="light"
          buttonText={`${
            userLoggedIn
              ? (t('home:uploadNow') as string)
              : (t('home:getStarted') as string)
          }`}
          link={`${userLoggedIn ? '/upload-landing' : '/register'}`}
        />
      </div>
    </div>
  );
};

AdeContestPage.getInitialProps = async (ctx: VirpPageContext) => {
  const getTranslations = await loadNamespaces({
    ...ctx,
    pathname: '/amsterdam-dance-event-demo-drops-2024',
  });

  return {
    ...getTranslations,
  };
};

export default AdeContestPage;

const Video = styled.iframe`
  border-radius: 5px;
`;
